import { CheckboxField, TextField, useFormContext, useFormField } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { Badge, BADGE_TYPE_INFO, BADGE_TYPE_NEUTRAL } from '@calendly/ui/components/badge'
import { BareButton } from '@calendly/ui/components/bare-button'
import { Button } from '@calendly/ui/components/button'
import { IconGripVertical } from '@calendly/ui/components/glyphs/IconGripVertical'
import { IconTrash } from '@calendly/ui/components/glyphs/IconTrash'
import { IconButton } from '@calendly/ui/components/icon-button'
import { LabelText } from '@calendly/ui/components/typography'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { CheckboxField as StandaloneCheckboxField } from '@calendly/ui/form/checkbox-field'
import { colorIconSecondary } from '@calendly/ui/theme'
import { useCallback, useEffect, useRef } from 'react'

import { useAdvancedSortable } from 'components/core/sortable'
import { allowsOtherChoices, hasAnswerChoices } from 'components/event_type_edit/invitee_questions'
import { useErrorAfterInitialTouch } from 'features/event_type_editor'

import { AnswerChoicesSection } from './answer_choices_section'
import {
  getAnswerTypeBadgeLabel,
  getCollapsedChoicePreview,
  getEditingQuestionFieldNames,
  isMultipleChoiceFormat,
  snapshotQuestionValues,
} from './helpers'
import { QuestionTypeMenu } from './question_type_menu'
import {
  CardContent,
  choicePreviewItemCss,
  choicePreviewListCss,
  customQuestionClickTargetCss,
  customQuestionCollapsedCss,
  customQuestionContentCss,
  DragHandle,
  ExpandedCard,
  ExpandedCardBody,
  ExpandedCardFooter,
  ExpandedCardHeader,
  customQuestionLeadingControlCss,
  expandedCardBodyWithSectionsCss,
  expandedCardFooterActionsCss,
  expandedCardFooterWithTypeCss,
  expandedCardHeaderTitleCss,
  expandedCardSettingSectionCss,
  questionTypeBadgeRowCss,
} from './styles'
import { validateEditingFields } from '../confirmation/validate_editing_fields'

export const CustomQuestionCard = ({
  disabled = false,
  editingKey,
  index,
  onCancel,
  onDelete,
  onDone,
  onEdit,
  question,
}) => {
  const {
    setFieldTouched,
    validateForm,
  } = useFormContext()
  const ref = useRef()
  const nameFieldName = `inviteeQuestions.${index}.name`
  const formatFieldName = `inviteeQuestions.${index}.format`
  const requiredFieldName = `inviteeQuestions.${index}.required`
  const includeOtherFieldName = `inviteeQuestions.${index}.include_other`
  const [{ value: format }, _, { setValue: setFormat }] = useFormField({ name: formatFieldName })
  const isEditing = editingKey === question.id
  const isNewQuestion = String(question.id).startsWith('new')
  const displayLabel = question.name || I18n.t('publisher.event_type_editor_crud.booking_page.new_question')
  const answerTypeBadgeLabel = getAnswerTypeBadgeLabel(format)
  const choicePreview = getCollapsedChoicePreview(question)
  const headerLabel = isNewQuestion
    ? I18n.t('publisher.event_type_editor_crud.booking_page.new_question')
    : I18n.t('publisher.event_type_editor_crud.booking_page.edit_question')

  const {
    sortable: {
      isDragging,
      attributes,
      listeners,
      setNodeRef,
    },
    itemStyle,
  } = useAdvancedSortable({ id: question.id })

  useErrorAfterInitialTouch(nameFieldName)

  useEffect(() => {
    if (!ref.current || !isEditing) return

    if (isNewQuestion) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      ref.current.focus()
    }
  }, [isEditing, isNewQuestion, question.id])

  const handleDone = useCallback(async () => {
    const hasErrors = await validateEditingFields({
      fieldNames: getEditingQuestionFieldNames(index, format),
      setFieldTouched,
      validateForm,
    })

    if (!hasErrors) {
      onDone()
    }
  }, [format, index, onDone, setFieldTouched, validateForm])

  if (isEditing) {
    return (
      <ExpandedCard
        ref={setNodeRef}
        data-testid={`custom-question-card-editing-${index}`}
        isDragging={isDragging}
        {...itemStyle}
      >
        <ExpandedCardHeader>
          <DragHandle {...attributes} {...listeners}>
            <UiIcon color={colorIconSecondary} icon={IconGripVertical} size="medium" title="" />
          </DragHandle>
          <CardContent>
            <LabelText className={expandedCardHeaderTitleCss} size="large">
              {headerLabel}
            </LabelText>
          </CardContent>
          <IconButton
            appearance="ghost"
            aria-label={I18n.t('publisher.action.remove')}
            icon={IconTrash}
            size="small"
            title={I18n.t('publisher.action.remove')}
            onClick={onDelete}
          />
        </ExpandedCardHeader>
        <div className={expandedCardBodyWithSectionsCss}>
          <ExpandedCardBody>
            <TextField
              ref={ref}
              autoFocus
              aria-label={I18n.t('publisher.event_type_editor_crud.booking_page.question_label')}
              label={I18n.t('publisher.event_type_editor_crud.booking_page.question_label')}
              maxLength={255}
              name={nameFieldName}
              placeholder={I18n.t('publisher.event_type_editor_crud.booking_page.question_placeholder')}
              required
              requiredMark="asterisk"
              type="textarea"
            />
            {hasAnswerChoices(format) && <AnswerChoicesSection index={index} />}
          </ExpandedCardBody>
          {isMultipleChoiceFormat(format) && (
            <div className={expandedCardSettingSectionCss}>
              <StandaloneCheckboxField
                checked={format === 'choices_many'}
                label={I18n.t('publisher.event_type_editor_crud.booking_page.allow_multiple_selections')}
                onChange={(checked) => setFormat(checked ? 'choices_many' : 'choices_one')}
              />
            </div>
          )}
          {allowsOtherChoices(format) && (
            <div className={expandedCardSettingSectionCss}>
              <CheckboxField
                label={I18n.t('publisher.event_types.invitee_questions.include_other_option')}
                name={includeOtherFieldName}
              />
            </div>
          )}
          <div className={expandedCardSettingSectionCss}>
            <CheckboxField
              label={I18n.t('publisher.event_types.invitee_questions.required_label')}
              name={requiredFieldName}
            />
          </div>
        </div>
        <ExpandedCardFooter className={expandedCardFooterWithTypeCss}>
          <QuestionTypeMenu questionIndex={index} />
          <div className={expandedCardFooterActionsCss}>
            <Button appearance="ghost" size="small" type="button" onClick={onCancel}>
              {I18n.t('publisher.action.cancel')}
            </Button>
            <Button appearance="primary" size="small" type="button" onClick={handleDone}>
              {I18n.t('publisher.action.done')}
            </Button>
          </div>
        </ExpandedCardFooter>
      </ExpandedCard>
    )
  }

  return (
    <div
      ref={setNodeRef}
      className={customQuestionCollapsedCss}
      data-testid={`custom-question-card-${index}`}
      {...itemStyle}
    >
      <span className={customQuestionLeadingControlCss}>
        <DragHandle {...attributes} {...listeners}>
          <UiIcon color={colorIconSecondary} icon={IconGripVertical} size="medium" title="" />
        </DragHandle>
      </span>
      <BareButton
        aria-label={I18n.t('publisher.event_type_editor_crud.booking_page.edit_question')}
        className={customQuestionContentCss}
        disabled={disabled}
        onClick={onEdit}
      >
        <span className={customQuestionClickTargetCss}>
          <LabelText color={question.name ? 'standard' : 'muted'} size="large">{displayLabel}</LabelText>
        </span>
        {choicePreview.mode === 'list' && (
          <ul className={choicePreviewListCss} data-testid={`choice-preview-${index}`}>
            {choicePreview.items.map(({ id, label }) => (
              <li key={id} className={choicePreviewItemCss}>
                <LabelText color="muted" size="medium">{label}</LabelText>
              </li>
            ))}
          </ul>
        )}
        {choicePreview.mode === 'count' && (
          <ul className={choicePreviewListCss} data-testid={`choice-preview-${index}`}>
            <li className={choicePreviewItemCss}>
              <LabelText color="muted" size="medium">
                {I18n.t('publisher.event_type_editor_crud.booking_page.choices_count', {
                  count: choicePreview.count,
                })}
              </LabelText>
            </li>
          </ul>
        )}
        <span className={questionTypeBadgeRowCss}>
          {answerTypeBadgeLabel && (
            <Badge type={BADGE_TYPE_INFO}>{answerTypeBadgeLabel}</Badge>
          )}
          {question.required && (
            <Badge type={BADGE_TYPE_NEUTRAL}>
              {I18n.t('publisher.event_types.invitee_questions.required_label')}
            </Badge>
          )}
        </span>
      </BareButton>
      <span className={customQuestionLeadingControlCss}>
        <IconButton
          appearance="ghost"
          aria-label={I18n.t('publisher.action.remove')}
          disabled={disabled}
          icon={IconTrash}
          size="small"
          title={I18n.t('publisher.action.remove')}
          onClick={onDelete}
        />
      </span>
    </div>
  )
}

export { snapshotQuestionValues }
